// The sync unit. An Edit says what changed in one project; a Change wraps it
// with who, when and an idempotency key. The device store applies Changes
// first (stage 1), the list's Durable Object applies them as the one writer
// (stage 2) and sync replays them (stage 3). Pure: the same Change on the
// same document always gives the same document, so applying it twice is
// applying it once.
//
// Clocks, as on iOS. Every item has its own editedAt, and the project has one
// for its own fields (title, request, deletion). An item edit is last-writer-
// wins on the item's clock and leaves the project's alone, so a tick made on
// one device can never make another device's rename lose. A stamp that is
// missing (a demo seed, an old archive) is older than everything. A deletion
// is a tombstone that merge keeps.
import { applyArtifact, applyStepFix, applySwap, type NewId } from './apply';
import type { Artifact, EquipmentSwap, StepFix } from './artifacts';
import { titleTrimmed, type Id, type Millis, type Project, type Step } from './plan';

export type Edit =
  /** A device-made project, or a visitor's projects moving into an account. */
  | { kind: 'create'; project: Project }
  | { kind: 'tick'; list: 'steps' | 'equipment'; itemId: Id; done: boolean }
  | { kind: 'rename'; title: string }
  | { kind: 'delete' }
  | { kind: 'restore' }
  /** A stage's artifact landing: only that artifact changes. */
  | { kind: 'graft'; artifact: Artifact }
  /** A snag's repair of a step: reworded and/or broken down, the step keeps its id. */
  | { kind: 'fix'; itemId: Id; fix: StepFix; note: string; photoHash?: string }
  /** A snag's stand-in for a tool or material. */
  | { kind: 'swap'; itemId: Id; swap: EquipmentSwap; note: string; photoHash?: string };

export interface Change {
  /** The idempotency key: a uuid made where the edit was made. */
  id: string;
  projectId: Id;
  at: Millis;
  deviceId: string;
  edit: Edit;
}

type Tick = Extract<Edit, { kind: 'tick' }>;

/**
 * The ids a change mints for the items it adds, derived from the change's
 * own id: the list's Durable Object and every device apply the same graft
 * or fix, and must agree on the ids of the steps it creates.
 */
export function seededIds(changeId: string): NewId {
  let n = 0;
  return () => `${changeId}.${n++}`;
}

/** A missing stamp is older than any real one. */
const clock = (stamp: Millis | undefined): number => stamp ?? Number.NEGATIVE_INFINITY;

/**
 * The document after this change. `current` is undefined when the device
 * has no copy: a create makes one, anything else has nothing to change.
 */
export function applyChange(current: Project | undefined, change: Change): Project | undefined {
  const { edit, at } = change;
  if (edit.kind === 'create') {
    if (edit.project.id !== change.projectId) throw new Error('create: the project is not the one the change names');
    // The incoming copy keeps the clock its edits earned; only a copy that
    // never had one is stamped now, so a later merge compares real edits.
    const incoming: Project = { ...edit.project, createdAt: edit.project.createdAt ?? at, editedAt: edit.project.editedAt ?? at };
    return current ? merge(current, incoming) : incoming;
  }
  if (!current) return undefined;
  switch (edit.kind) {
    case 'tick':
      return tick(current, edit, at);
    case 'rename':
      if (at < clock(current.editedAt)) return current;
      return { ...current, title: titleTrimmed(edit.title), editedAt: at };
    case 'delete':
      if (at < clock(current.editedAt)) return current;
      return { ...current, deletedAt: at, editedAt: at };
    case 'restore': {
      if (current.deletedAt === undefined || at <= current.deletedAt || at < clock(current.editedAt)) return current;
      const { deletedAt: _cleared, ...rest } = current;
      return { ...rest, editedAt: at };
    }
    case 'graft':
      return applyArtifact(current, edit.artifact, at, seededIds(change.id));
    case 'fix':
      return applyStepFix(current, edit.itemId, edit.fix, edit.note, edit.photoHash, at, seededIds(change.id));
    case 'swap':
      return applySwap(current, edit.itemId, edit.swap, edit.note, edit.photoHash, at);
    default: {
      const unknown: never = edit;
      throw new Error(`unknown edit kind: ${(unknown as { kind: string }).kind}`);
    }
  }
}

function tick(p: Project, edit: Tick, at: Millis): Project {
  const items = p[edit.list];
  const index = items.findIndex((i) => i.id === edit.itemId);
  if (index < 0) return p; // an item this copy has not seen yet; the owner of the edit still has it
  const item = items[index]!;
  if (at < clock(item.editedAt)) return p;
  const next = [...items];
  next[index] = { ...item, done: edit.done, editedAt: at };
  return { ...p, [edit.list]: next };
}

/**
 * Two copies of one project, reconciled: the copy edited later supplies the
 * project-level fields, each step and tool is last-writer-wins on its own
 * clock, items either copy has are kept, the earlier createdAt stands, and a
 * tombstone on either side survives. Commutative and idempotent, so it is
 * safe to run in any order and any number of times.
 */
export function merge(a: Project, b: Project): Project {
  if (a.id !== b.id) throw new Error('merge: two different projects');
  const [newer, older] = byClock(a, b);
  const result: Project = { ...newer, steps: mergeItems(newer.steps, older.steps), equipment: mergeItems(newer.equipment, older.equipment) };
  const editedAt = extreme(Math.max, a.editedAt, b.editedAt);
  const createdAt = extreme(Math.min, a.createdAt, b.createdAt);
  const deletedAt = extreme(Math.max, a.deletedAt, b.deletedAt);
  if (editedAt !== undefined) result.editedAt = editedAt;
  if (createdAt !== undefined) result.createdAt = createdAt;
  if (deletedAt !== undefined) result.deletedAt = deletedAt;
  return result;
}

/** Newer first. A tie is broken by content so the answer is the same from either side. */
function byClock<T extends { editedAt?: Millis }>(a: T, b: T): [T, T] {
  const ca = clock(a.editedAt), cb = clock(b.editedAt);
  if (ca !== cb) return ca > cb ? [a, b] : [b, a];
  return JSON.stringify(a) >= JSON.stringify(b) ? [a, b] : [b, a];
}

function mergeItems(primary: Step[], secondary: Step[]): Step[] {
  const twins = new Map(secondary.map((s) => [s.id, s]));
  const merged = primary.map((item) => { const twin = twins.get(item.id); return twin ? pick(item, twin) : item; });
  const seen = new Set(primary.map((s) => s.id));
  for (const item of secondary) if (!seen.has(item.id)) merged.push(item);
  return merged;
}

function pick(x: Step, y: Step): Step {
  const cx = clock(x.editedAt), cy = clock(y.editedAt);
  if (cx !== cy) return cx > cy ? x : y;
  if (x.done !== y.done) return x.done ? x : y; // the same instant: a tick beats an untick, nothing punishes
  return JSON.stringify(x) >= JSON.stringify(y) ? x : y;
}

function extreme(by: (...n: number[]) => number, a: Millis | undefined, b: Millis | undefined): Millis | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return by(a, b);
}
